import type { ComponentDefinition } from "../types";

export const SOLAR: ComponentDefinition[] = [
  {
    id: "solar-zamp-obsidian-100",
    name: "Zamp Solar Obsidian 100W",
    shortName: "Obsidian 100W",
    subtitle: "Monocrystalline solar panel 100W",
    category: "solar",
    manufacturer: "Zamp Solar",
    model: "USM2004",
    // Exact USM2004_Data_V4 sheet, p1 (also supplied with the customer request):
    // https://cdn.shopify.com/s/files/1/0344/2973/0953/files/USM2004_Data_V4.pdf?v=1626117061
    // Keep Vmp/Imp distinct from Voc=26.15 V and Isc=4.82 A; older listings differ.
    watts: 100,
    voltage: 21.71,
    pvVoc: 26.15,
    ampOutput: 4.59,
    peakSunHoursDefault: 4,
    width: 180,
    height: 100,
    // ATP connector poles; the sheet does not publish harness ampacity or a
    // maximum series-fuse rating. Zero retains unknown terminal ampacity.
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 0,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 0,
      },
    ],
    image: "solar-zamp-obsidian-100.webp",
  },
  {
    id: "solar-generic",
    name: "Generic Solar Panel",
    shortName: "Generic Panel",
    subtitle: "Configurable solar panel",
    category: "solar",
    watts: 100,
    voltage: 18.6,
    pvVoc: 22.5,
    ampOutput: 5.4,
    peakSunHoursDefault: 4,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
  },
  {
    id: "solar-renogy-100",
    name: "Renogy 100W Mono",
    shortName: "100W Mono",
    subtitle: "Solar panel 100W",
    category: "solar",
    manufacturer: "Renogy",
    model: "RNG-100D-SS",
    watts: 100,
    voltage: 18.6,
    pvVoc: 22.3,
    // Legacy RNG-100D-SS (18.6 Vmp), manufacturer electrical data:
    // https://uk.renogy.com/content/files/Manuals/RNG-100D-SS-Datasheet.pdf
    pvMaxSeriesFuseA: 15,
    ampOutput: 5.38,
    peakSunHoursDefault: 4,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
    image: "solar-renogy-100.webp",
  },
  {
    id: "solar-renogy-200",
    name: "Renogy 200W Mono",
    shortName: "200W Mono",
    subtitle: "Solar panel 200W",
    category: "solar",
    manufacturer: "Renogy",
    model: "RSP200D",
    watts: 200,
    voltage: 22.6,
    pvVoc: 27,
    ampOutput: 8.85,
    peakSunHoursDefault: 4,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-renogy-200.webp",
  },
  {
    id: "solar-renogy-320",
    name: "Renogy 320W Mono",
    shortName: "320W Mono",
    subtitle: "Solar panel 320W",
    category: "solar",
    manufacturer: "Renogy",
    model: "RNG-320D",
    watts: 320,
    // Renogy RNG-320D datasheet V1.4, p2: 32.7 V Vmp / 9.79 A Imp; Voc remains 40.10 V.
    voltage: 32.7,
    pvVoc: 40.1,
    ampOutput: 9.79,
    peakSunHoursDefault: 4,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-renogy-320.webp",
  },
  {
    id: "solar-sunpower-maxeon-415",
    name: "SunPower MAXEON 3 415W",
    shortName: "MAXEON 415W",
    subtitle: "Solar panel 415W",
    category: "solar",
    manufacturer: "SunPower",
    model: "SPR-MAX3-415",
    watts: 415,
    voltage: 35.3,
    pvVoc: 40.7,
    ampOutput: 11.75,
    peakSunHoursDefault: 4,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-sunpower-maxeon-415.webp",
  },
  {
    id: "solar-victron-100",
    name: "Victron 100W Mono",
    shortName: "100W Mono",
    subtitle: "Solar panel 100W",
    category: "solar",
    manufacturer: "Victron",
    model: "BlueSolar 100W-12V",
    watts: 100,
    voltage: 18,
    pvVoc: 22.4,
    ampOutput: 5.56,
    peakSunHoursDefault: 4,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
    image: "solar-victron-100.webp",
  },
  {
    id: "solar-richsolar-200",
    name: "Rich Solar 200W Mono",
    shortName: "200W Mono",
    subtitle: "Solar panel 200W",
    category: "solar",
    manufacturer: "Rich Solar",
    model: "RS-M200",
    watts: 200,
    voltage: 18.4,
    pvVoc: 21.8,
    ampOutput: 10.9,
    peakSunHoursDefault: 4,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-richsolar-200.webp",
  },
  {
    id: "solar-bougerv-200-flex",
    name: "BougeRV 200W Flexible",
    shortName: "200W Flex",
    subtitle: "Flexible solar panel 200W",
    category: "solar",
    manufacturer: "BougeRV",
    model: "Yuma 200 (CIGS)",
    watts: 200,
    voltage: 24,
    pvVoc: 30.4,
    ampOutput: 8.52,
    peakSunHoursDefault: 3.5,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-bougerv-200-flex.webp",
  },
  {
    id: "solar-newpowa-100",
    name: "Newpowa 100W Mono",
    shortName: "100W Mono",
    subtitle: "Solar panel 100W",
    category: "solar",
    manufacturer: "Newpowa",
    model: "NPA100S-12I",
    watts: 100,
    voltage: 19.23,
    pvVoc: 22.12,
    ampOutput: 5.21,
    peakSunHoursDefault: 4,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
    image: "solar-newpowa-100.webp",
  },
  {
    id: "solar-100",
    name: "100 W Solar Panel",
    shortName: "100W Panel",
    subtitle: "Solar panel 100W",
    category: "solar",
    watts: 100,
    voltage: 18,
    pvVoc: 21.6,
    ampOutput: 5.6,
    peakSunHoursDefault: 4,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
  },
  {
    id: "solar-flex-100",
    name: "Flexible 100 W Panel",
    shortName: "Flex 100W",
    subtitle: "Flexible solar panel 100W",
    category: "solar",
    watts: 100,
    voltage: 18,
    pvVoc: 21.6,
    ampOutput: 5.6,
    peakSunHoursDefault: 3.5,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
  },
  {
    id: "solar-victron-115",
    name: "Victron 115W Mono",
    shortName: "115W Mono",
    subtitle: "Solar panel 115W",
    category: "solar",
    manufacturer: "Victron",
    model: "BlueSolar 115W-12V",
    watts: 115,
    voltage: 19,
    pvVoc: 23.32,
    ampOutput: 6.04,
    peakSunHoursDefault: 4,
    width: 185,
    height: 105,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
    image: "solar-victron-115.webp",
  },
  {
    id: "solar-victron-175",
    name: "Victron 175W Mono",
    shortName: "175W Mono",
    subtitle: "Solar panel 175W",
    category: "solar",
    manufacturer: "Victron",
    model: "BlueSolar 175W-12V",
    watts: 175,
    voltage: 19.4,
    pvVoc: 23.7,
    ampOutput: 9.03,
    peakSunHoursDefault: 4,
    width: 195,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-victron-175.webp",
  },
  {
    id: "solar-victron-215",
    name: "Victron 215W Mono",
    shortName: "215W Mono",
    subtitle: "Solar panel 215W",
    category: "solar",
    manufacturer: "Victron",
    model: "BlueSolar 215W-24V",
    watts: 215,
    voltage: 40.1,
    pvVoc: 46.01,
    ampOutput: 5.36,
    peakSunHoursDefault: 4,
    width: 205,
    height: 115,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-victron-215.webp",
  },
  {
    id: "solar-victron-305",
    name: "Victron 305W Mono",
    shortName: "305W Mono",
    subtitle: "Solar panel 305W",
    category: "solar",
    manufacturer: "Victron",
    model: "BlueSolar 305W-20V",
    watts: 305,
    voltage: 32.5,
    pvVoc: 39.7,
    ampOutput: 9.38,
    peakSunHoursDefault: 4,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-victron-305.webp",
  },
  {
    id: "solar-victron-360",
    name: "Victron 360W Mono",
    shortName: "360W Mono",
    subtitle: "Solar panel 360W",
    category: "solar",
    manufacturer: "Victron",
    model: "BlueSolar 360W-24V",
    watts: 360,
    voltage: 38.4,
    pvVoc: 47.4,
    ampOutput: 9.38,
    peakSunHoursDefault: 4,
    width: 230,
    height: 125,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-victron-360.webp",
  },
  {
    id: "solar-jinko-440",
    name: "Jinko JKM440M-6TL4 440W",
    shortName: "Jinko 440W",
    subtitle: "Solar panel 440W",
    category: "solar",
    manufacturer: "Jinko",
    model: "JKM440M-6TL4-V",
    watts: 440,
    voltage: 33.82,
    pvVoc: 40.92,
    ampOutput: 13.01,
    peakSunHoursDefault: 4,
    width: 195,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 20,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 20,
      },
    ],
    image: "solar-jinko-440.webp",
  },
  {
    id: "solar-renogy-175-flex",
    name: "Renogy 175W Flexible",
    shortName: "175W Flex",
    subtitle: "Flexible solar panel 175W",
    category: "solar",
    manufacturer: "Renogy",
    model: "RNG-175DB-H",
    watts: 175,
    voltage: 20.3,
    pvVoc: 24.9,
    ampOutput: 8.75,
    peakSunHoursDefault: 3.5,
    width: 195,
    height: 110,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-renogy-175-flex.webp",
  },
  {
    id: "solar-renogy-200-flex",
    name: "Renogy 200W Flexible",
    shortName: "200W Flex",
    subtitle: "Flexible solar panel 200W",
    category: "solar",
    manufacturer: "Renogy",
    model: "RSP200DB-72",
    watts: 200,
    voltage: 20.1,
    pvVoc: 23.9,
    ampOutput: 10.02,
    peakSunHoursDefault: 3.5,
    width: 205,
    height: 115,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-renogy-200-flex.webp",
  },
  {
    id: "solar-renogy-400-bifacial",
    name: "Renogy 400W Bifacial",
    shortName: "400W Bifacial",
    subtitle: "Bifacial solar panel 400W",
    category: "solar",
    manufacturer: "Renogy",
    model: "RSP400DCT",
    watts: 400,
    voltage: 34,
    pvVoc: 39.42,
    ampOutput: 11.76,
    peakSunHoursDefault: 4.2,
    width: 240,
    height: 130,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "solar-renogy-400-bifacial.webp",
  },
  {
    id: "solar-renogy-550",
    name: "Renogy 550W Mono",
    shortName: "550W Mono",
    subtitle: "Solar panel 550W",
    category: "solar",
    manufacturer: "Renogy",
    model: "RSP550E",
    watts: 550,
    voltage: 41.97,
    pvVoc: 49.95,
    ampOutput: 13.11,
    peakSunHoursDefault: 4,
    width: 250,
    height: 135,
    terminals: [
      {
        id: "pos",
        label: "+",
        polarity: "positive",
        direction: "bidi",
        side: "bottom",
        offset: 0.35,
        maxAmp: 20,
      },
      {
        id: "neg",
        label: "−",
        polarity: "negative",
        direction: "bidi",
        side: "bottom",
        offset: 0.65,
        maxAmp: 20,
      },
    ],
    image: "solar-renogy-550.webp",
  },
];
